import { css, html, LitElement } from "lit";
import type { PixelIconName } from "../shared/pixel-icons";
import { windowsMePrimitives } from "../shared/windows-me-styles";
import "../desktop-shortcut/desktop-shortcut";

export type DesktopShellShortcut = {
	description?: string;
	icon?: PixelIconName;
	id: string;
	label: string;
};

export class DesktopShell extends LitElement {
	static properties = {
		selectedShortcutId: { type: String, attribute: "selected-shortcut-id" },
		shortcuts: { attribute: false },
		title: { type: String },
	};

	static styles = [
		windowsMePrimitives,
		css`
			:host {
				position: relative;
				display: block;
				inline-size: 100%;
				block-size: 100dvh;
				overflow: clip;
				color: #ffffff;
				background-color: var(
					--z-desktop-wallpaper-color,
					var(--desktop-field-color)
				);
				background-image: var(--z-desktop-wallpaper-image, none);
				background-repeat: var(--z-desktop-wallpaper-repeat, no-repeat);
				background-position: top left;
				background-size: var(--z-desktop-wallpaper-size, auto);
			}

			.skip-link {
				position: absolute;
				inset-block-start: 6px;
				inset-inline-start: 6px;
				z-index: 50;
				padding: 4px 6px;
				color: var(--shell-text);
				background: var(--shell-surface);
				border: 1px solid var(--shell-surface-dark);
				transform: translateY(-180%);
			}

			.skip-link:focus {
				transform: translateY(0);
			}

			.desktop {
				position: relative;
				inline-size: 100%;
				block-size: 100%;
				padding:
					calc(env(safe-area-inset-top, 0px) + 10px)
					calc(env(safe-area-inset-right, 0px) + 10px)
					calc(var(--shell-dock-height) + env(safe-area-inset-bottom, 0px) + 10px)
					calc(env(safe-area-inset-left, 0px) + 10px);
				box-sizing: border-box;
			}

			.shortcut-layer {
				position: absolute;
				inset: 8px 12px calc(var(--shell-dock-height) + 10px) 8px;
				display: grid;
				grid-auto-flow: column;
				grid-auto-columns: 76px;
				grid-template-rows: repeat(auto-fill, minmax(86px, 1fr));
				align-content: start;
				gap: 10px 8px;
				pointer-events: auto;
			}

			.window-layer {
				position: absolute;
				inset: 8px 8px calc(var(--shell-dock-height) + 8px);
				min-inline-size: 0;
				min-block-size: 0;
				pointer-events: none;
			}

			.window-layer > slot::slotted(*) {
				pointer-events: auto;
			}

			.overlay-layer {
				position: absolute;
				inset: 0;
				z-index: 60;
				pointer-events: none;
			}

			.overlay-layer > slot::slotted(*) {
				pointer-events: auto;
			}

			.dock-layer {
				position: absolute;
				inset-inline: 0;
				inset-block-end: 0;
				z-index: 45;
				pointer-events: none;
			}

			@media (max-width: 599px) {
				.shortcut-layer {
					inset-inline-end: 8px;
					grid-auto-flow: row;
					grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
					grid-template-rows: none;
				}

				.window-layer { inset-inline-start: 8px; }
			}
		`,
	];

	selectedShortcutId = "";

	shortcuts: DesktopShellShortcut[] = [];

	title = "Desktop";

	render() {
		return html`
			<a class="skip-link focus-ring" href="#desktop-window-layer">Skip to active application</a>
			<section
				class="desktop"
				role="application"
				aria-label=${this.title}
				@click=${this.#handleDesktopClick}
				@keydown=${this.#handleDesktopKeyDown}
			>
				${
					this.shortcuts.length
						? html`
							<div class="shortcut-layer" role="list" aria-label="Desktop shortcuts">
								${this.shortcuts.map(
									(shortcut) => html`
										<desktop-shortcut
											role="listitem"
											.description=${shortcut.description ?? ""}
											.icon=${shortcut.icon ?? "radio"}
											.label=${shortcut.label}
											.selected=${this.selectedShortcutId === shortcut.id}
											shortcut-id=${shortcut.id}
											@desktop-shortcut-open=${this.#handleShortcutOpen}
											@desktop-shortcut-select=${this.#handleShortcutSelect}
										></desktop-shortcut>
									`,
								)}
							</div>
						`
						: null
				}
				<div class="window-layer" id="desktop-window-layer">
					<slot name="windows"></slot>
				</div>
				<div class="overlay-layer">
					<slot name="overlay"></slot>
				</div>
				<div class="dock-layer">
					<slot name="dock"></slot>
				</div>
			</section>
		`;
	}

	#handleDesktopClick(event: MouseEvent) {
		const path = event.composedPath();
		if (!path.includes(this)) return;
		const clickedShortcut = path.some(
			(target) =>
				target instanceof HTMLElement && target.tagName === "DESKTOP-SHORTCUT",
		);
		if (clickedShortcut) return;
		this.selectedShortcutId = "";
		this.dispatchEvent(
			new CustomEvent("desktop-selection-clear", {
				bubbles: true,
				composed: true,
			}),
		);
	}

	#handleDesktopKeyDown(event: KeyboardEvent) {
		if (!this.shortcuts.length) return;
		if (
			!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(event.key)
		)
			return;
		event.preventDefault();

		const currentIndex = Math.max(
			0,
			this.shortcuts.findIndex(
				(shortcut) => shortcut.id === this.selectedShortcutId,
			),
		);
		const delta =
			event.key === "ArrowRight" || event.key === "ArrowDown" ? 1 : -1;
		const nextIndex =
			(currentIndex + delta + this.shortcuts.length) % this.shortcuts.length;
		const next = this.shortcuts[nextIndex] ?? this.shortcuts[0];
		if (!next) return;
		this.selectedShortcutId = next.id;
		this.requestUpdate();
		queueMicrotask(() => {
			this.renderRoot
				.querySelectorAll<HTMLElement>("desktop-shortcut")
				.item(nextIndex)
				?.shadowRoot?.querySelector<HTMLElement>("button")
				?.focus();
		});
		this.dispatchEvent(
			new CustomEvent("desktop-shortcut-highlight", {
				bubbles: true,
				composed: true,
				detail: { shortcutId: next.id },
			}),
		);
	}

	#handleShortcutOpen(event: CustomEvent<{ shortcutId: string }>) {
		this.selectedShortcutId = event.detail.shortcutId;
		this.dispatchEvent(
			new CustomEvent("desktop-shell-launch-shortcut", {
				bubbles: true,
				composed: true,
				detail: event.detail,
			}),
		);
	}

	#handleShortcutSelect(event: CustomEvent<{ shortcutId: string }>) {
		this.selectedShortcutId = event.detail.shortcutId;
		this.dispatchEvent(
			new CustomEvent("desktop-shell-select-shortcut", {
				bubbles: true,
				composed: true,
				detail: event.detail,
			}),
		);
	}
}

if (!customElements.get("desktop-shell")) {
	customElements.define("desktop-shell", DesktopShell);
}
